import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Select } from '@/components/ui/select';
import { v2Api } from '@/lib/api';
import type { ExercisePhasePayload } from '@/lib/api/v2';
import type { IScenarioDetail } from '@/types/scenarios';
import { Plus, Pencil, Clock, Trash2 } from 'lucide-react';

interface ExercisePhasesCardProps {
  scenario: IScenarioDetail;
  onRefresh: () => Promise<unknown>;
  locked?: boolean;
}

const emptyForm: ExercisePhasePayload & { order: number; duration_mins: number } = {
  phase: 0,
  order: 1,
  duration_mins: 0,
};

export function ExercisePhasesCard({ scenario, onRefresh, locked = false }: ExercisePhasesCardProps) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [phaseToDelete, setPhaseToDelete] = useState<number | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [phaseCatalog, setPhaseCatalog] = useState<Array<{ id: number; name: string; slug: string }>>([]);

  const phases = useMemo(() => scenario.phases ?? [], [scenario.phases]);
  const exercisePhases = scenario.exercise_phases ?? [];
  const exerciseId = scenario.id;
  const phaseOptions = useMemo(() => {
    const byId = new Map<number, { id: number; name: string; slug: string }>();
    for (const p of phases) byId.set(p.id, { id: p.id, name: p.name, slug: p.slug });
    for (const p of phaseCatalog) {
      if (!byId.has(p.id)) byId.set(p.id, p);
    }
    return Array.from(byId.values()).sort((a, b) => a.id - b.id);
  }, [phases, phaseCatalog]);
  const phaseNameById = useMemo(
    () => new Map(phaseOptions.map((p) => [p.id, p.name])),
    [phaseOptions],
  );

  useEffect(() => {
    let mounted = true;
    const loadPhases = async () => {
      if (phases.length > 0) return;
      try {
        const list = await v2Api.dashboard.phases.list();
        if (!mounted) return;
        setPhaseCatalog(
          list.map((p) => ({
            id: p.id,
            name: p.name,
            slug: p.slug,
          })),
        );
      } catch {
        if (mounted) setPhaseCatalog([]);
      }
    };
    void loadPhases();
    return () => {
      mounted = false;
    };
  }, [phases.length]);

  const openCreate = () => {
    if (locked) return;
    setEditingId(null);
    setForm({
      phase: phases[0]?.id ?? 0,
      order: exercisePhases.length + 1,
      duration_mins: 0,
    });
    setError(null);
    setDialogOpen(true);
  };

  const openEdit = (ep: typeof exercisePhases[0]) => {
    if (locked) return;
    const phaseId = Number(ep.phase);
    setEditingId(ep.id);
    setForm({
      phase: Number.isFinite(phaseId) ? phaseId : 0,
      order: ep.order,
      duration_mins: ep.duration_mins,
    });
    setError(null);
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setEditingId(null);
    setError(null);
  };

  const handleSave = async () => {
    if (!form.phase) {
      setError('Please select a phase.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      if (editingId !== null) {
        await v2Api.dashboard.phases.exercisePhases.update(exerciseId, editingId, {
          phase: form.phase,
          order: form.order,
          duration_mins: form.duration_mins,
        });
      } else {
        await v2Api.dashboard.phases.exercisePhases.create(exerciseId, {
          phase: form.phase,
          order: form.order,
          duration_mins: form.duration_mins,
        });
      }
      closeDialog();
      await onRefresh();
    } catch (err: unknown) {
      const res = (err as { response?: { data?: { detail?: string; errors?: Record<string, string[] | string> } } })?.response;
      const data = res?.data;
      const errors = data?.errors;
      const nfe = errors?.non_field_errors;
      const nonField = Array.isArray(nfe) ? nfe : [];
      const firstMsg = nonField[0];
      const isUniqueError = typeof firstMsg === 'string' && (
        firstMsg.includes('exercise_id') && firstMsg.includes('phase') && firstMsg.toLowerCase().includes('unique')
      );
      const message = isUniqueError
        ? 'This phase is already linked to this scenario. Each phase can only be added once.'
        : (firstMsg ?? data?.detail ?? (err instanceof Error ? err.message : 'Failed to save'));
      setError(typeof message === 'string' ? message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteClick = (id: number) => {
    if (locked) return;
    setPhaseToDelete(id);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (phaseToDelete === null) return;
    setSaving(true);
    setError(null);
    try {
      await v2Api.dashboard.phases.exercisePhases.delete(exerciseId, phaseToDelete);
      await onRefresh();
      setDeleteDialogOpen(false);
      setPhaseToDelete(null);
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ??
        (err instanceof Error ? err.message : 'Failed to delete');
      setError(message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-lg">Exercise Phases</CardTitle>
        <Button
          type="button"
          variant="default"
          size="sm"
          onClick={openCreate}
          disabled={locked || phaseOptions.length === 0}
          title={
            locked
              ? 'Exercise phases are locked while a scenario run is active'
              : phaseOptions.length === 0
                ? 'Add phases in Phases settings first'
                : 'Create exercise phase'
          }
          className="shrink-0"
        >
          <Plus className="h-4 w-4 mr-1" />
          Create
        </Button>
      </CardHeader>
      <CardContent>
        {exercisePhases.length === 0 ? (
          <p className="text-sm text-muted-foreground py-4">
            No exercise phases yet. Add phases to define order and duration for this scenario.
          </p>
        ) : (
          <ul className="space-y-2">
            {exercisePhases
              .slice()
              .sort((a, b) => a.order - b.order || a.id - b.id)
              .map((ep) => (
                <li
                  key={ep.id}
                  className="flex items-center justify-between gap-2 p-3 rounded-lg border bg-muted/30 hover:bg-muted/50"
                >
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm truncate">
                      {phaseNameById.get(ep.phase) ?? `Phase ${ep.phase}`}
                    </p>
                    <div className="flex items-center gap-3 text-xs text-muted-foreground mt-0.5">
                      <span className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {ep.duration_mins} min
                      </span>
                      <span>Order: {ep.order}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => openEdit(ep)}
                      title="Edit"
                      disabled={saving || locked}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-destructive hover:text-destructive"
                      onClick={() => handleDeleteClick(ep.id)}
                      title="Delete"
                      disabled={saving || locked}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </li>
              ))}
          </ul>
        )}
      </CardContent>

      <Dialog open={dialogOpen} onOpenChange={(open) => !open && closeDialog()}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>{editingId !== null ? 'Edit exercise phase' : 'Add exercise phase'}</DialogTitle>
            <DialogDescription>
              Link a phase to this scenario with order and duration.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            {error && (
              <p className="text-sm text-destructive">{error}</p>
            )}
            <div className="grid gap-2">
              <Label htmlFor="ep-phase">Phase</Label>
              <Select
                id="ep-phase"
                value={form.phase ? String(form.phase) : ''}
                onChange={(e) => setForm((f) => ({ ...f, phase: parseInt(e.target.value, 10) || 0 }))}
              >
                <option value="">Select phase</option>
                {phaseOptions.map((p) => (
                  <option key={p.id} value={String(p.id)}>
                    {p.name} ({p.slug})
                  </option>
                ))}
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="ep-order">Order</Label>
                <Input
                  id="ep-order"
                  type="number"
                  min={1}
                  value={form.order}
                  onChange={(e) => setForm((f) => ({ ...f, order: parseInt(e.target.value, 10) || 1 }))}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="ep-duration">Duration (min)</Label>
                <Input
                  id="ep-duration"
                  type="number"
                  min={0}
                  value={form.duration_mins}
                  onChange={(e) => setForm((f) => ({ ...f, duration_mins: parseInt(e.target.value, 10) || 0 }))}
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={closeDialog} disabled={saving}>
              Cancel
            </Button>
            <Button type="button" onClick={handleSave} disabled={saving || locked}>
              {saving ? 'Saving…' : editingId !== null ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={deleteDialogOpen}
        onOpenChange={(open) => {
          if (!open) {
            setDeleteDialogOpen(false);
            setPhaseToDelete(null);
          }
        }}
      >
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Delete exercise phase</DialogTitle>
            <DialogDescription>
              Delete this exercise phase? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setDeleteDialogOpen(false);
                setPhaseToDelete(null);
              }}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={handleDeleteConfirm}
              disabled={saving}
            >
              {saving ? 'Deleting…' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
